import React, { useState, useCallback, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, SelectChangeEvent, Typography, useTheme, Button, IconButton, Tooltip, styled, Autocomplete, Chip, Badge, ToggleButtonGroup, ToggleButton } from '@mui/material';
import BaseDialog from '../dialogs/BaseDialog';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ViewSettingsDialog, { CardSize, CardRadius, isViewDefault } from '../library/ViewSettingsDialog';
import debounce from 'lodash/debounce';
import { keyframes } from '@emotion/react';
import Tips from '../dialogs/Tips';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Platform } from '../../types/Game';
import { PlatformFilterType } from '../../services/api';

interface GameListFilterProps {
  onSearch: (query: string) => void;
  onOrderChange: (order: string) => void;
  onClearFilter: () => void;
  totalGames: number;
  initialSearchQuery: string;
  initialOrder: string;
  onPriorityModeChange?: (enabled: boolean) => void;
  priorityMode?: boolean;
  currentStatus?: number;
  platforms?: Platform[];
  selectedPlatforms?: number[];
  onPlatformChange?: (platforms: number[]) => void;
  platformFilterType?: PlatformFilterType;
  onPlatformFilterTypeChange?: (filterType: PlatformFilterType) => void;
  onFilterApply?: (platforms: number[], filterType: PlatformFilterType) => void;
  cardSize?: CardSize;
  uiVisible?: boolean;
  titleVisible?: boolean;
  cardRadius?: CardRadius;
  isViewNonDefault?: boolean;
  onCardSizeChange?: (size: CardSize) => void;
  onUiVisibleChange?: (visible: boolean) => void;
  onTitleVisibleChange?: (visible: boolean) => void;
  onCardRadiusChange?: (radius: CardRadius) => void;
  onViewReset?: () => void;
  // フォルダ：アイコンを押すとフォルダ一覧ポップオーバーを開く（オーナーのみ）
  onFolderClick?: (anchor: HTMLElement) => void;
  folderActive?: boolean;
}

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const StyledPriorityButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderRadius: '4px',
  padding: '6px',
  transition: 'all 0.2s ease',
  '&.active': {
    background: 'linear-gradient(45deg, #2088ff, #a455ff)',
    backgroundSize: '200% 200%',
    animation: `${gradientAnimation} 3s ease infinite`,
    boxShadow: '0 2px 8px rgba(32, 136, 255, 0.3)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    '&.active': {
      background: 'linear-gradient(45deg, #2088ff, #a455ff)',
      backgroundSize: '200% 200%',
      animation: `${gradientAnimation} 3s ease infinite`,
      filter: 'brightness(1.1)',
    },
  },
}));

const PriorityTipsContent = () => (
  <Box sx={{ maxWidth: 400 }}>
    <Typography variant="body2" paragraph>
      プライオリティは、積みゲー/中断/ウィッシュリストのゲームに優先度を設定できる機能です。
    </Typography>
    <Typography variant="body2" paragraph>
      使い方：
    </Typography>
    <Box component="ol" sx={{ pl: 2, mt: 1 }}>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          エディットモード（<TuneIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をオンにすると、各ゲームにプライオリティスライダーが表示されます
        </Typography>
      </li>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          スライダーで0-5の優先度を設定できます
        </Typography>
      </li>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          プライオリティボタン（<AutoAwesomeIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をクリックすると、優先度順に並び替えができます
        </Typography>
      </li>
    </Box>
    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
      ※プレイ済み/プレイ中のゲームではプライオリティ機能は使用できません
    </Typography>
  </Box>
);

const FilterTypeTipsContent = () => (
  <Box sx={{ maxWidth: 400 }}>
    <Typography variant="body2" paragraph>
      プラットフォームフィルターには2つのモードがあります。
    </Typography>
    <Box component="ul" sx={{ pl: 2, mt: 1 }}>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>ユーザーステータスPF</strong>：ユーザーがゲームに設定したプラットフォーム（どのハードでプレイしたか）でフィルタリングします
        </Typography>
      </li>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>ゲーム発売PF</strong>：ゲーム自体の発売プラットフォームでフィルタリングします（例：PS5版が存在するゲーム）
        </Typography>
      </li>
    </Box>
  </Box>
);

const GameListFilter: React.FC<GameListFilterProps> = ({ onSearch, onOrderChange, onClearFilter, totalGames, initialSearchQuery, initialOrder, onPriorityModeChange, priorityMode = false, currentStatus = 1, platforms = [], selectedPlatforms = [], onPlatformChange, platformFilterType = 'user_platform', onPlatformFilterTypeChange, onFilterApply, cardSize = 'standard', uiVisible = true, titleVisible = true, cardRadius = 'standard', isViewNonDefault = false, onCardSizeChange, onUiVisibleChange, onTitleVisibleChange, onCardRadiusChange, onViewReset, onFolderClick, folderActive = false }) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);
  const [isPriorityActive, setIsPriorityActive] = useState(initialOrder === 'priority_desc');
  const [previousStatus, setPreviousStatus] = useState(currentStatus);
  const [wasActivePriority, setWasActivePriority] = useState(false);
  const [wasEditMode, setWasEditMode] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [tempSelectedPlatforms, setTempSelectedPlatforms] = useState<number[]>(selectedPlatforms);
  const [tempFilterType, setTempFilterType] = useState<PlatformFilterType>(platformFilterType);
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);
  const theme = useTheme();

  // デバウンス処理を行う関数を作成
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      onSearch(query);
    }, 1000),
    [onSearch]
  );

  // 初期値の更新を監視
  useEffect(() => {
    setSearchQuery(initialSearchQuery);
    setOrder(initialOrder);
    setIsPriorityActive(initialOrder === 'priority_desc');
  }, [initialSearchQuery, initialOrder]);

  // selectedPlatformsの変更を監視してtempSelectedPlatformsを更新
  useEffect(() => {
    setTempSelectedPlatforms(selectedPlatforms);
  }, [selectedPlatforms]);

  // ステータス変更時の処理を追加
  useEffect(() => {
    if (currentStatus !== previousStatus) {
      if (currentStatus === 0 || currentStatus === 1 || currentStatus === 2) {
        // ステータス1,2に変更された場合
        if (order === 'priority_desc') {
          setWasActivePriority(true);
          setOrder('default');
          onOrderChange('default');
        }
        // エディットモードの状態を保存してOFFに
        if (priorityMode) {
          setWasEditMode(true);
          onPriorityModeChange?.(false);
        }
      } else if (previousStatus === 0 || previousStatus === 1 || previousStatus === 2) {
        // ステータス0,1,2から他のステータスに変更された場合
        if (wasActivePriority) {
          setOrder('priority_desc');
          onOrderChange('priority_desc');
          setIsPriorityActive(true);
          setWasActivePriority(false);
        }
        // エディットモードを元の状態に戻す
        if (wasEditMode) {
          onPriorityModeChange?.(true);
          setWasEditMode(false);
        }
      }
      setPreviousStatus(currentStatus);
    }
  }, [currentStatus, order, onOrderChange, priorityMode, onPriorityModeChange]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    // ステータス1,2の場合、プライオリティ順は選択できないようにする
    if ((currentStatus === 0 || currentStatus === 1 || currentStatus === 2) && newOrder === 'priority_desc') {
      return;
    }
    setOrder(newOrder);
    onOrderChange(newOrder);
    setIsPriorityActive(false);
  };

  const handlePriorityClick = () => {
    // ステータス1,2の場合、プライオリティ順は選択できないようにする
    if (currentStatus === 0 || currentStatus === 1 || currentStatus === 2) {
      return;
    }

    // プライオリティボタンの状態を切り替え
    const newPriorityActive = !isPriorityActive;
    setIsPriorityActive(newPriorityActive);

    // 並び順を更新
    const newOrder = newPriorityActive ? 'priority_desc' : 'default';
    setOrder(newOrder);
    onOrderChange(newOrder);
  };

  const clearFilter = () => {
    setSearchQuery('');
    setOrder('default');
    setIsPriorityActive(false);
    onPlatformChange?.([]);
    onClearFilter();
  };

  // フィルターダイアログを開く
  const handleOpenFilterDialog = () => {
    setTempSelectedPlatforms(selectedPlatforms);
    setTempFilterType(platformFilterType);
    setFilterDialogOpen(true);
  };

  // フィルターダイアログを閉じる
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  // フィルターを適用
  const handleApplyFilter = () => {
    if (onFilterApply) {
      // 統合ハンドラがある場合は一度に更新
      onFilterApply(tempSelectedPlatforms, tempFilterType);
    } else {
      // 後方互換性のため個別ハンドラも維持
      onPlatformChange?.(tempSelectedPlatforms);
      onPlatformFilterTypeChange?.(tempFilterType);
    }
    setFilterDialogOpen(false);
  };

  // フィルターをリセット
  const handleResetFilter = () => {
    setTempSelectedPlatforms([]);
    setTempFilterType('user_platform');
  };

  // 検索クエリが空でない場合またはプラットフォームが選択されている場合にフィルターがアクティブ
  const isFilterActive = searchQuery !== '' || selectedPlatforms.length > 0;

  // フィルターの数をカウント
  const activeFilterCount = selectedPlatforms.length;

  // orderの表示テキストをマッピング
  const orderDisplayText: { [key: string]: string } = {
    'default': 'デフォルト',
    'rating': '評価 (低い順)',
    'rating_desc': '評価 (高い順)',
    'play_time': 'プレイ時間 (短い順)',
    'play_time_desc': 'プレイ時間 (長い順)',
    'play_start_date': 'プレイ開始日 (古い順)',
    'play_start_date_desc': 'プレイ開始日 (新しい順)',
    'release_date': '発売日 (古い順)',
    'release_date_desc': '発売日 (新しい順)',
    'added_date': '追加日 (古い順)',
    'added_date_desc': '追加日 (新しい順)',
    'update_date': '更新日 (古い順)',
    'update_date_desc': '更新日 (新しい順)',
    'priority_desc': 'PRIORITY',
    'priority': '優先度 (低い順)'
  };

  // スマホ用の短い表示テキスト
  const orderDisplayTextShort: { [key: string]: string } = {
    'default': 'Sort',
    'rating': '評価↑',
    'rating_desc': '評価↓',
    'play_time': '時間↑',
    'play_time_desc': '時間↓',
    'play_start_date': '開始日↑',
    'play_start_date_desc': '開始日↓',
    'release_date': '発売日↑',
    'release_date_desc': '発売日↓',
    'added_date': '追加日↑',
    'added_date_desc': '追加日↓',
    'update_date': '更新日↑',
    'update_date_desc': '更新日↓',
    'priority_desc': 'PRIORITY',
    'priority': '優先度↑'
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          placeholder="Filter..."
          variant="standard"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            minWidth: 60,
            '& .MuiInputBase-input': {
              ...theme.typography.body2,
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 0.5
            }
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {/* フォルダ */}
          {onFolderClick && (
            <Tooltip title="フォルダ">
              <IconButton
                size="small"
                onClick={(e) => onFolderClick(e.currentTarget)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  padding: '6px',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                }}
              >
                <FolderOutlinedIcon
                  sx={{
                    color: folderActive ? 'primary.main' : 'text.secondary',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {/* フィルターボタン */}
          <Tooltip title={currentStatus === 0 || currentStatus === 1 || currentStatus === 2 ? "プライオリティは積みゲー/中断/ウィッシュリストで適用できます" : "プライオリティ"}>
            <StyledPriorityButton
              onClick={handlePriorityClick}
              size="small"
              className={isPriorityActive ? 'active' : ''}
              disabled={currentStatus === 0 || currentStatus === 1 || currentStatus === 2}
              sx={{
                opacity: currentStatus === 0 || currentStatus === 1 || currentStatus === 2 ? 0.5 : 1,
              }}
            >
              <AutoAwesomeIcon
                sx={{
                  color: isPriorityActive ? '#fff' : 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  filter: isPriorityActive ? 'drop-shadow(0 0 2px rgba(255,255,255,0.5))' : 'none',
                }}
              />
            </StyledPriorityButton>
          </Tooltip>
          {onPriorityModeChange && (
            <Tooltip title={currentStatus === 0 || currentStatus === 1 || currentStatus === 2 ? "エディットモードは積みゲー/中断/ウィッシュリストで適用できます" : "エディットモード"}>
              <IconButton
                size="small"
                onClick={() => onPriorityModeChange(!priorityMode)}
                sx={{
                  backgroundColor: priorityMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  padding: '6px',
                  opacity: currentStatus === 0 || currentStatus === 1 || currentStatus === 2 ? 0.5 : 1,
                }}
                disabled={currentStatus === 0 || currentStatus === 1 || currentStatus === 2}
              >
                <TuneIcon
                  sx={{
                    color: priorityMode ? 'text.primary' : 'text.secondary',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {platforms.length > 0 && onPlatformChange && (
            <Tooltip title="フィルター">
              <IconButton
                size="small"
                onClick={handleOpenFilterDialog}
                sx={{
                  backgroundColor: activeFilterCount > 0 ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: activeFilterCount > 0 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  }
                }}
              >
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.6rem',
                      minWidth: '14px',
                      height: '14px',
                      display: activeFilterCount > 0 ? 'flex' : 'none',
                    }
                  }}
                >
                  <FilterAltOutlinedIcon
                    sx={{
                      color: activeFilterCount > 0 ? 'text.secondary' : 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {onCardSizeChange && onUiVisibleChange && (
            <Tooltip title="表示設定">
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  onClick={() => setViewSettingsOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    borderRadius: '4px',
                    padding: '6px',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                  }}
                >
                  <ViewModuleOutlinedIcon
                    sx={{
                      color: isViewNonDefault ? 'text.primary' : 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                    }}
                  />
                </IconButton>
                {isViewNonDefault && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 3,
                      right: 3,
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: 'text.disabled',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          )}
          <Select
            value={order}
            onChange={handleOrderChange}
            displayEmpty
            size="small"
            sx={{
              minWidth: 80,
              height: { xs: '28px', sm: '32px' },
              ...theme.typography.body2,
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '4px',
              '& .MuiSelect-select': {
                paddingY: 0,
                paddingX: 1,
                paddingRight: { xs: '0px !important', sm: '32px !important' },
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
              '& .MuiSelect-icon': {
                display: { xs: 'none', sm: 'block' }
              }
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <SortIcon sx={{
                  mr: 0.5,
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  flexShrink: 0
                }} />
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {orderDisplayText[selected] || '並び替え'}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'block', sm: 'none' }
                  }}
                >
                  {orderDisplayTextShort[selected] || '並び替え'}
                </Box>
              </Box>
            )}
          >
            <MenuItem value="default" dense>デフォルト</MenuItem>
            <MenuItem value="rating_desc" dense>評価 (高い順)</MenuItem>
            <MenuItem value="rating" dense>評価 (低い順)</MenuItem>
            <MenuItem value="play_time_desc" dense>プレイ時間 (長い順)</MenuItem>
            <MenuItem value="play_time" dense>プレイ時間 (短い順)</MenuItem>
            <MenuItem value="play_start_date_desc" dense>プレイ開始日 (新しい順)</MenuItem>
            <MenuItem value="play_start_date" dense>プレイ開始日 (古い順)</MenuItem>
            <MenuItem value="release_date_desc" dense>発売日 (新しい順)</MenuItem>
            <MenuItem value="release_date" dense>発売日 (古い順)</MenuItem>
            <MenuItem value="added_date_desc" dense>追加日 (新しい順)</MenuItem>
            <MenuItem value="added_date" dense>追加日 (古い順)</MenuItem>
            <MenuItem value="update_date_desc" dense>更新日 (新しい順)</MenuItem>
            <MenuItem value="update_date" dense>更新日 (古い順)</MenuItem>
            {/* <MenuItem value="priority_desc" dense>優先度 (高い順)</MenuItem>
            <MenuItem value="priority" dense>優先度 (低い順)</MenuItem> */}
          </Select>
          <Tips
                title="TIPS:プライオリティ"
                content={<PriorityTipsContent />}
                buttonComponent={
                  <IconButton
                    size="small"
                    sx={{
                      padding: '4px',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                }
              />
        </Box>
      </Box>
      {isFilterActive && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {totalGames}件
            </Typography>
            {selectedPlatforms.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selectedPlatforms.map((pid) => {
                  const platform = platforms.find(p => p.platform_id === pid);
                  return platform ? (
                    <Chip
                      key={pid}
                      label={platform.name}
                      size="small"
                      onDelete={() => onPlatformChange?.(selectedPlatforms.filter(id => id !== pid))}
                      sx={{ fontSize: '0.65rem', height: '20px' }}
                    />
                  ) : null;
                })}
              </Box>
            )}
          </Box>
          <Button
            size="small"
            onClick={clearFilter}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* フィルターダイアログ */}
      <BaseDialog
        open={filterDialogOpen}
        onClose={handleCloseFilterDialog}
        title="フィルター"
        actions={
          <>
            <Button onClick={handleResetFilter} color="inherit" size="small">
              リセット
            </Button>
            <Button onClick={handleCloseFilterDialog} color="inherit" size="small">
              キャンセル
            </Button>
            <Button onClick={handleApplyFilter} variant="contained" size="small">
              適用
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            プラットフォーム
          </Typography>
          <Autocomplete
            multiple
            options={platforms}
            getOptionLabel={(option) => option.name}
            value={platforms.filter((p) => tempSelectedPlatforms.includes(p.platform_id))}
            onChange={(_, newValue) => {
              setTempSelectedPlatforms(newValue.map((p) => p.platform_id));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="選択してください"
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name}
                  {...getTagProps({ index })}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))
            }
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              フィルタータイプ
            </Typography>
            <Tips
              title="フィルタータイプについて"
              content={<FilterTypeTipsContent />}
            />
          </Box>
          <ToggleButtonGroup
            value={tempFilterType}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setTempFilterType(newValue);
              }
            }}
            size="small"
            sx={{ mb: 2, width: '100%' }}
          >
            <ToggleButton
              value="user_platform"
              sx={{
                flex: 1,
                fontSize: '0.75rem',
                textTransform: 'none',
                py: 0.5
              }}
            >
              ステータスPF
            </ToggleButton>
            <ToggleButton
              value="release_platform"
              sx={{
                flex: 1,
                fontSize: '0.75rem',
                textTransform: 'none',
                py: 0.5
              }}
            >
              ゲームPF
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </BaseDialog>

      {onCardSizeChange && onUiVisibleChange && onCardRadiusChange && onTitleVisibleChange && onViewReset && (
        <ViewSettingsDialog
          open={viewSettingsOpen}
          onClose={() => setViewSettingsOpen(false)}
          cardSize={cardSize}
          uiVisible={uiVisible}
          titleVisible={titleVisible}
          cardRadius={cardRadius}
          onCardSizeChange={onCardSizeChange}
          onUiVisibleChange={onUiVisibleChange}
          onTitleVisibleChange={onTitleVisibleChange}
          onCardRadiusChange={onCardRadiusChange}
          onReset={onViewReset}
        />
      )}
    </Box>
  );
};

export default GameListFilter;
